import { axios } from '@/plugins/http-axios';
import { defineStore } from 'pinia';
import { IOfficesOfRegion } from '@ifr/form-questionnaire';
import { IRole } from './user';
import type { IAgent } from '@/types/appTypes';
import { useMainStore } from './mainStore';
import Vue from 'vue';

export interface ISysParameter {
  lkbSystemsVisible: boolean;
  code: string;
  desc: string;
  value: string;
  resulttype: string;
}

export interface ITakers {
  id: string;
  fio: string;
  roleName: string;
}

export interface IStatusBank {
  name: string;
  count: number;
  id: number;
}

export interface ISigner {
  id: string;
  fio: string;
  roleName: string;
}

interface ICommonBank {
  id: number;
  name: string;
  inn: string;
  kpp: string;
}

export interface IBaseOffice {
  id: number;
  name: string;
  city: string;
  shortaddress: string;
  fulladdress: string;
}

export interface IBaseOffice2 extends IBaseOffice {
  cftcode: string;
  cftcalendar: string;
  bik: string;
  korr: string;
  korrlocation: string;
  branchcode: string;
  code: string;
  sortedItem: number;
  bankid: number;
  hide: boolean;
}

export interface IBankOffice extends IBaseOffice2 {
  regionid: number;
  region: IRegion;
  // реквизиты для печатных форм - есть не у всех офисов
  placeResolution?: string;
  placeSigning?: string;
  fullNameEmpBank?: string;
  positionEmpBank?: string;
  numberProxyEmpBank?: string;
  dateProxyEmpBank?: string;
  additionalInfo?: string;
}

export interface IBank2 extends ICommonBank {
  okpo: string;
  ogrn: string;
  regions: IBankRegion[];
}

export interface IBankRegion {
  id: number;
  name: string;
  hide: boolean;
  orderPosition?: number;
  offices: IBankOffice[];
}

export interface IRegion {
  hide: boolean;
  id: number;
  name: string;
  /** у части регионов нет (на stage.cred - 1 из 12) */
  orderPosition?: number;
}

export interface ISysSetting {
  code: string;
  desc: string;
  value: boolean;
  affectedSystems: string[];
}

interface IListItemFilter {
  [key: string]: any[];
}

interface IBankState {
  takers: ITakers[];
  statuses: IStatusBank[];
  signers: ISigner[];
  officesList: IBankOffice[];
  regionsList: IRegion[];
  sysParameters: ISysParameter[];
  sysSettings: ISysSetting[];
  bankingRegions: IOfficesOfRegion[];
  roles: IRole[];
  listItemsFilter: IListItemFilter;
}

/** По orderPosition; регион без него - в конец (раньше NaN в сравнении давал случайный порядок). */
function byOrderPosition(a: { orderPosition?: number }, b: { orderPosition?: number }): number {
  const pa = a.orderPosition ?? Number.MAX_SAFE_INTEGER;
  const pb = b.orderPosition ?? Number.MAX_SAFE_INTEGER;
  return pa - pb;
}

export const useBankStore = defineStore('bank', {
  state: (): IBankState => ({
    takers: [],
    statuses: [],
    signers: [],
    officesList: [],
    regionsList: [],
    sysParameters: [],
    sysSettings: [],
    bankingRegions: [],
    roles: [],
    listItemsFilter: {
      statusid: [],
      takers: [],
      offices: [],
      signers: [],
      applicationRequestTypes: [],
      agents: [],
    },
  }),

  getters: {
    isBankingRegions: (state) => !!state.bankingRegions.length,
    isRoles: (state) => !!state.roles.length,
    listRoles: (state) =>
      state.roles.map((role) => ({
        text: role.name || 'Нет имени',
        value: role.id,
      })),
    sysSetting:
      (state) =>
      (key: string): boolean => {
        const o = state.sysSettings.find((par: ISysSetting) => par.code === key);
        return o ? o.value : false;
      },
  },

  actions: {
    setSysSettings(data: ISysSetting[]) {
      this.sysSettings = data;
    },

    addListItemsFilter(key: string, items: IListItemFilter[string] = []) {
      Vue.set(this.listItemsFilter, key, items);
    },
    // SystemParameter.vue передаёт сюда и строковые системные параметры
    setSysSettingsValue({ code = '', value = false }: { code?: string; value?: boolean | string } = {}) {
      const sysPar = this.sysSettings.find((par) => par.code === code);
      if (sysPar) {
        sysPar.value = value as boolean;
      }
    },

    setSysParameters(data: ISysParameter[]) {
      this.sysParameters = data;
    },

    setTakers(data: ITakers[]) {
      this.takers = data;
    },

    setStatuses(statuses: IStatusBank[]) {
      this.statuses = statuses;
    },

    setSigners(data: ISigner[]) {
      this.signers = data;
    },

    setOffices(offices: IBankOffice[]) {
      this.officesList = offices;
    },

    setRegions(regions: IRegion[]) {
      this.regionsList = regions.filter((region) => ![3, 27, 425].includes(region.id)).sort(byOrderPosition);
    },

    _setBankingRegions(regions: IBankRegion[]) {
      regions.sort(byOrderPosition);
      this.bankingRegions = regions.map<IOfficesOfRegion>((region) => {
        region.offices.sort((a, b) => a.sortedItem - b.sortedItem);
        return { text: region.name, value: region.offices };
      });
    },

    setRoles(data: IRole[]) {
      this.roles = data;
    },

    async getSysParameters(): Promise<ISysParameter[]> {
      const mainStore = useMainStore();

      if (this.sysParameters.length) {
        return this.sysParameters;
      }

      try {
        const resp = await axios.get<ISysParameter[]>(mainStore.getUri.sysParameters);
        this.setSysParameters(resp.data);
        return resp.data;
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getTakers(): Promise<void> {
      const mainStore = useMainStore();

      if (this.takers.length) {
        return Promise.resolve();
      }

      try {
        const resp = await axios.get<ITakers[]>(mainStore.getUri.usertakers);
        this.setTakers(resp.data);
      } catch (e: any) {
        return Promise.reject(e);
      }
    },
    async getAgents(): Promise<void> {
      const mainStore = useMainStore();

      if (this.listItemsFilter.agents.length) {
        return Promise.resolve();
      }

      try {
        const resp = await axios.get<IAgent[]>(mainStore.getUri.agentSave);
        const list = resp.data.map((item) => ({
          text: item.shortName,
          value: item.id,
        }));
        this.addListItemsFilter('agents', list);
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getStatusCount(): Promise<void> {
      const mainStore = useMainStore();

      try {
        const resp = await axios.get<IStatusBank[]>(mainStore.getUri.applicationStatuscount);
        this.setStatuses(resp.data);
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getSigners(): Promise<void> {
      const mainStore = useMainStore();

      if (this.signers.length) {
        return Promise.resolve();
      }

      try {
        const resp = await axios.get<ISigner[]>(mainStore.getUri.userSigner);
        this.setSigners(resp.data);
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getOffices(): Promise<IBankOffice[]> {
      const mainStore = useMainStore();

      if (this.officesList.length) {
        return this.officesList;
      }

      try {
        const resp = await axios.get<IBankOffice[]>(mainStore.getUri.office);
        this.setOffices(resp.data);
        return this.officesList;
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getRegions(): Promise<IRegion[]> {
      const mainStore = useMainStore();

      if (this.regionsList.length) {
        return this.regionsList;
      }

      try {
        const resp = await axios.get<IRegion[]>(mainStore.getUri.region);
        this.setRegions(resp.data);
        return this.regionsList;
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getBankingRegions(): Promise<IOfficesOfRegion[]> {
      const mainStore = useMainStore();

      if (this.isBankingRegions) {
        return this.bankingRegions;
      }

      try {
        const resp = await axios.get<IBank2[]>(mainStore.getUri.bankOffices);
        this._setBankingRegions(resp.data[0].regions);
        return this.bankingRegions;
      } catch (e: any) {
        return Promise.reject(e);
      }
    },

    async getAllRoles(): Promise<IRole[]> {
      const mainStore = useMainStore();

      if (this.isRoles) {
        return this.roles;
      }

      const url = mainStore.getUri.roles;
      try {
        const resp = await axios.get<IRole[]>(url);
        this.setRoles(resp.data);
        return resp.data;
      } catch {
        return this.roles;
      }
    },

    async getSystemSetting(): Promise<void> {
      const mainStore = useMainStore();

      if (this.sysSettings.length) {
        return Promise.resolve();
      }

      try {
        const resp = await axios.get<ISysSetting[]>(mainStore.getUri.mainsysset);
        this.setSysSettings(resp.data);
      } catch (e: any) {
        return Promise.reject(e);
      }
    },
  },
});

export default useBankStore;
